/*
 * Description:
 * ---------------------
 * Main file for the Weavian Edge Console
 *
 * Redesigned with sidebar navigation, interactive network canvas,
 * and progressive-disclosure UX.
 *
 * ! file App.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2025
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchWithTimeout } from "./utils/fetchWithTimeout";
import { readApproval, readNode, readPanel, syncNode } from "./appView";
import { viewOffered } from "./navigation";
import TourOverlay from "./tour/TourOverlay";
import "./AppLayout.css";
import Sidebar from "./components/Sidebar";
import NodeMenu from "./components/NodeMenu";
import RegionMenu from "./components/RegionMenu";
import { REGIONS } from "./regions";
import DashboardView from "./components/DashboardView";
import OverviewPanel from "./components/OverviewPanel";
import TrafficPanels, { ClassifierSetting } from "./components/TrafficPanels";
import CloudPanel from "./components/CloudPanel";
import HealthPanel from "./components/HealthPanel";
import SeqForecastPanel from "./components/SeqForecastPanel";
import BondingPanel from "./components/BondingPanel";
import EncryptionPanel from "./components/EncryptionPanel";
import ObfuscationPanel from "./components/ObfuscationPanel";
import FlowExitsPanel from "./components/FlowExitsPanel";
import ASPathPanel from "./components/ASPathPanel";
import VerificationPanel from "./components/VerificationPanel";
import InterfaceTrafficPanel from "./components/InterfaceTrafficPanel";
import { useSession } from "./components/PortalGate";
import AccessPanel from "./components/AccessPanel";
import AlertsPanel from "./components/AlertsPanel";
import AssistantSettingsPanel from "./components/AssistantSettingsPanel";
import ThreatIntelPanel from "./components/ThreatIntelPanel";
import InvestigationsPanel from "./components/InvestigationsPanel";
import ChangesPanel from "./components/ChangesPanel";
import { CHANGES_HERO } from "./components/changeWords";
import TrustFabric from "./components/TrustFabric";
import OutsideAgentsPanel from "./components/OutsideAgentsPanel";
import ApproversPanel from "./components/ApproversPanel";
import ApprovalPage from "./components/ApprovalPage";
import AgentRulesPanel from "./components/AgentRulesPanel";
import NeedsYou, { useWaiting } from "./components/NeedsYou";
import { APPROVERS_HERO, NEEDS_HERO, NEEDS_YOU, RULES_HERO } from "./components/approvalWords";
import UpdatesPanel from "./components/UpdatesPanel";
import AccountPanel from "./components/AccountPanel";
import { ACCESS_LEAD, ACCOUNT, accountLead } from "./components/accessWords";
import RemoteDevicesPanel from "./components/RemoteDevicesPanel";
import {
  API_ROUTE,
  joinUrl,
  resolveApiBase,
  resolveProxyBase,
  nodeApiUrlFor,
} from "./serverApi";
import NodeSummaryPanel from "./components/NodeSummaryPanel";
import EventLogPanel from "./components/EventLogPanel";
import QoSPanel from "./components/QoSPanel";
import AdaButton from "./components/AdaButton";
import DiagOverviewPanel from "./components/DiagOverviewPanel";
import DiagWANPanel from "./components/DiagWANPanel";
import WifiPanel from "./components/WifiPanel";
import HomeWifiPanel from "./components/HomeWifiPanel";
import HomeWifiFleetPanel from "./components/HomeWifiFleetPanel";
import DiagDevicesPanel from "./components/DiagDevicesPanel";
import StressTestPanel from "./components/StressTestPanel";
import AQMPanel from "./components/AQMPanel";
import FlowContinuityPanel from "./components/FlowContinuityPanel";
import TakPanel from "./components/TakPanel";
import DatasetPanel from "./components/DatasetPanel";
import WanPriorityPanel from "./components/WanPriorityPanel";
import RemoteAccessPanel from "./components/RemoteAccessPanel";
import PilotInsightsPanel from "./components/PilotInsightsPanel";
import PowerPanel from "./components/PowerPanel";
import DeviceLimitsPanel from "./components/DeviceLimitsPanel";
import DataBudgetsPanel from "./components/DataBudgetsPanel";
import FirewallPanel from "./components/FirewallPanel";
import DeepTelemetryPanel from "./components/DeepTelemetryPanel";
import ErrorBoundary from "./components/ErrorBoundary";
import ToastProvider, { useToast } from "./components/ToastProvider";
import { WanNamesProvider } from "./components/WanNames";
import ConfirmProvider, { useConfirm } from "./components/ConfirmModal";
import PhoneTabs, { phonePlaces } from "./components/PhoneTabs";
import usePhoneLayout from "./hooks/usePhoneLayout";
import { FiMenu } from "react-icons/fi";
import { isAnchorNode, isMobileNode } from "./utils/nodeKind";
import EdgeUnreachableNotice from "./components/EdgeUnreachableNotice";

// ── Traffic class definitions ────────────────────────────────────────────────

const TRAFFIC_CLASSES = [
  // Category classes
  { key: "CRITICAL", label: "Critical", hint: "Life-safety or mission critical control", isApp: false },
  { key: "REALTIME", label: "Realtime", hint: "Voice, real-time control loops", isApp: false },
  { key: "INTERACTIVE", label: "Interactive", hint: "Human-in-the-loop applications", isApp: false },
  { key: "STREAMING", label: "Streaming", hint: "Video, live streams, large flows", isApp: false },
  { key: "BULK", label: "Bulk", hint: "Backups, large downloads", isApp: false },
  { key: "BACKGROUND", label: "Background", hint: "Deferred or non-urgent traffic", isApp: false },
  { key: "BE", label: "Best Effort", hint: "Default routing class", isApp: false },
  { key: "DIAG", label: "Diagnostics", hint: "Telemetry, probes, diagnostics", isApp: false },
  // Application classes
  { key: "APP_TAK", label: "TAK", hint: "ATAK, WinTAK and TAK servers, by port 8087 and 8089 · category: Critical", isApp: true, category: "CRITICAL" },
  { key: "APP_ZOOM", label: "Zoom", hint: "Zoom meetings · category: Realtime", isApp: true, category: "REALTIME" },
  { key: "APP_GOOGLE_MEET", label: "Google Meet", hint: "Google Meet calls · category: Realtime", isApp: true, category: "REALTIME" },
  { key: "APP_MSTEAMS", label: "MS Teams", hint: "Microsoft Teams · category: Realtime", isApp: true, category: "REALTIME" },
  { key: "APP_WHATSAPP_CALL", label: "WhatsApp Call", hint: "WhatsApp voice/video · category: Realtime", isApp: true, category: "REALTIME" },
  { key: "APP_SIGNAL", label: "Signal", hint: "Signal messenger · category: Realtime", isApp: true, category: "REALTIME" },
  { key: "APP_DISCORD", label: "Discord", hint: "Discord voice/chat · category: Realtime", isApp: true, category: "REALTIME" },
  { key: "APP_NETFLIX", label: "Netflix", hint: "Netflix streaming · category: Streaming", isApp: true, category: "STREAMING" },
  { key: "APP_YOUTUBE", label: "YouTube", hint: "YouTube video · category: Streaming", isApp: true, category: "STREAMING" },
  { key: "APP_TWITCH", label: "Twitch", hint: "Twitch streaming · category: Streaming", isApp: true, category: "STREAMING" },
  { key: "APP_SPOTIFY", label: "Spotify", hint: "Spotify music · category: Streaming", isApp: true, category: "STREAMING" },
  { key: "APP_TIKTOK", label: "TikTok", hint: "TikTok video · category: Streaming", isApp: true, category: "STREAMING" },
  { key: "APP_AMAZON_VIDEO", label: "Amazon Video", hint: "Amazon Prime Video · category: Streaming", isApp: true, category: "STREAMING" },
  { key: "APP_DISNEYPLUS", label: "Disney+", hint: "Disney+ streaming · category: Streaming", isApp: true, category: "STREAMING" },
  { key: "APP_HULU", label: "Hulu", hint: "Hulu streaming · category: Streaming", isApp: true, category: "STREAMING" },
  { key: "APP_HBO", label: "HBO Max", hint: "HBO Max / Max streaming · category: Streaming", isApp: true, category: "STREAMING" },
  { key: "APP_SLACK", label: "Slack", hint: "Slack messaging · category: Interactive", isApp: true, category: "INTERACTIVE" },
  { key: "APP_TELEGRAM", label: "Telegram", hint: "Telegram messenger · category: Interactive", isApp: true, category: "INTERACTIVE" },
  { key: "APP_FACEBOOK", label: "Facebook", hint: "Facebook social · category: Interactive", isApp: true, category: "INTERACTIVE" },
  { key: "APP_INSTAGRAM", label: "Instagram", hint: "Instagram social · category: Interactive", isApp: true, category: "INTERACTIVE" },
  { key: "APP_LINKEDIN", label: "LinkedIn", hint: "LinkedIn professional · category: Interactive", isApp: true, category: "INTERACTIVE" },
  { key: "APP_SNAPCHAT", label: "Snapchat", hint: "Snapchat messaging · category: Interactive", isApp: true, category: "INTERACTIVE" },
  { key: "APP_GITHUB", label: "GitHub", hint: "GitHub dev platform · category: Interactive", isApp: true, category: "INTERACTIVE" },
  { key: "APP_APPLE", label: "Apple", hint: "Apple services · category: Interactive", isApp: true, category: "INTERACTIVE" },
  { key: "APP_OOKLA", label: "Ookla Speedtest", hint: "Speedtest by Ookla · category: Bulk", isApp: true, category: "BULK" },
  { key: "APP_DROPBOX", label: "Dropbox", hint: "Dropbox sync · category: Bulk", isApp: true, category: "BULK" },
  { key: "APP_STEAM", label: "Steam", hint: "Steam gaming · category: Realtime", isApp: true, category: "REALTIME" },
];

// ── API configuration ────────────────────────────────────────────────────────

// ── Single-edge (on-device) mode ─────────────────────────────────────────────
// When the dashboard is hosted BY one edge appliance (pilot customers), it
// talks only to that device's own API under same-origin /console/api/<path>,
// with no notabox-server, no node selector, and no fleet/multi-node views.
const SINGLE_EDGE_RAW =
  (typeof window !== "undefined" && window.REACT_APP_SINGLE_EDGE) ||
  process.env.REACT_APP_SINGLE_EDGE ||
  "";
export const IS_SINGLE_EDGE =
  String(SINGLE_EDGE_RAW) === "1" || String(SINGLE_EDGE_RAW).toLowerCase() === "true";
export const CONSOLE_API_PREFIX = "/console/api";
const LOCAL_NODE_ID = "this-device";
// Where the console opens: the fleet, or on a box's own console its dashboard.
const HOME = IS_SINGLE_EDGE ? "dashboard" : "nodes";

// ── Main App ─────────────────────────────────────────────────────────────────

function AppContent() {
  // ?panel= lets Ada land the operator on the panel a change is visible in,
  // rather than telling them where to click and hoping.
  const [activeView, setActiveView] = useState(
    () => readPanel() || (readApproval() ? "approval" : "") || HOME
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const phoneLayout = usePhoneLayout();
  const mainRef = useRef(null);
  const [interfaces, setInterfaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [health, setHealth] = useState({ status: "unknown" });
  const [nodes, setNodes] = useState([]);
  const [nodesLoading, setNodesLoading] = useState(true);
  const [nodesError, setNodesError] = useState("");
  // ?node= names the edge to open on. Ada links here from a proposal, and the
  // edge she was talking about is rarely the one that sorts first — without
  // this the operator lands on a different appliance and reads its settings as
  // though they were the answer.
  const [selectedNode, setSelectedNode] = useState(
    () => (IS_SINGLE_EDGE ? LOCAL_NODE_ID : readNode())
  );
  // The change an approver's emailed link opens (#approval=), or the one
  // picked from Needs you; none shows everything waiting for this person.
  const [approvalId, setApprovalId] = useState(readApproval);
  const [selectedTc, setSelectedTc] = useState(TRAFFIC_CLASSES[0]?.key);
  const [currentMap, setCurrentMap] = useState({});
  const [desiredMap, setDesiredMap] = useState({});
  // The order a class shows while the box takes it. Every change to a class's
  // connections is applied as it is made, and this holds the new order on
  // screen until the box answers with it.
  const [pendingMap, setPendingMap] = useState({});
  // How the last change to a class's connections went: state is "saving",
  // "saved" or "failed".
  const [orderSave, setOrderSave] = useState({ tc: null, state: "idle", error: "" });
  const [resetting, setResetting] = useState(false);
  const [bulkApplying, setBulkApplying] = useState(false);
  // Categories the operator has individually customized this session. The bulk
  // baseline-order action never overwrites these — individual rules win.
  const [customizedClasses, setCustomizedClasses] = useState(() => new Set());
  // Per-class scheduling action (spread|failover) from GET /policy/action. Drives
  // the WAN Priority editor's mode picker: SPREAD bonds across the selected
  // connections (order ignored); FAILOVER uses them in order (top-to-bottom).
  const [policyActions, setPolicyActions] = useState({});
  const [modeBusyTc, setModeBusyTc] = useState(null);
  const [lastNodesUpdate, setLastNodesUpdate] = useState(null);
  const [classFilter, setClassFilter] = useState("all");
  const [trafficStats, setTrafficStats] = useState({});
  const [categoryPrioritiesLoaded, setCategoryPrioritiesLoaded] = useState(false);

  const toast = useToast();
  const confirm = useConfirm();
  const session = useSession();

  const resolvedApiBase = useMemo(() => resolveApiBase(), []);
  const resolvedProxyBase = useMemo(() => resolveProxyBase(), []);

  const serverApiUrl = useCallback(
    (path) => {
      if (resolvedProxyBase) {
        return joinUrl(resolvedProxyBase, `/internal/api/v1${path}`);
      }
      return `${resolvedApiBase}${API_ROUTE}${path}`;
    },
    [resolvedApiBase, resolvedProxyBase]
  );

  // What waits for this person's yes, for the Needs you strip above every
  // screen and the approval screen's list. The appliance's own console has
  // no server to ask.
  const waiting = useWaiting(serverApiUrl, !IS_SINGLE_EDGE && session.managed);
  const openApproval = useCallback((id) => {
    setApprovalId(id);
    setActiveView("approval");
  }, []);

  // Resolution lives in serverApi.js because the assistant window renders
  // outside this tree and needs the identical mapping — see nodeApiUrlFor.
  const nodeApiUrl = useCallback(
    (path) => nodeApiUrlFor(selectedNode)(path),
    [selectedNode]
  );

  const logError = (message, err) => {
    if (err) console.error(`[Weavian] ${message}`, err);
    else console.error(`[Weavian] ${message}`);
  };

  const selectedDesired = useMemo(
    () => {
      // An order on its way to the box first, then the box's own
      const explicit = pendingMap[selectedTc] || desiredMap[selectedTc] || [];
      if (explicit.length > 0) return explicit;
      // If app class has no explicit priorities, inherit from parent category
      const tcDef = TRAFFIC_CLASSES.find((c) => c.key === selectedTc);
      if (tcDef?.isApp && tcDef.category) {
        return pendingMap[tcDef.category] || desiredMap[tcDef.category] || [];
      }
      return [];
    },
    [pendingMap, desiredMap, selectedTc]
  );

  // Track whether the current class is inheriting from its category
  const isInheritingPriority = useMemo(() => {
    const explicit = pendingMap[selectedTc] || desiredMap[selectedTc] || [];
    if (explicit.length > 0) return false;
    const tcDef = TRAFFIC_CLASSES.find((c) => c.key === selectedTc);
    if (!tcDef?.isApp || !tcDef.category) return false;
    const catList = pendingMap[tcDef.category] || desiredMap[tcDef.category] || [];
    return catList.length > 0;
  }, [pendingMap, desiredMap, selectedTc]);

  const inheritedFromCategory = useMemo(() => {
    if (!isInheritingPriority) return null;
    const tcDef = TRAFFIC_CLASSES.find((c) => c.key === selectedTc);
    return tcDef?.category || null;
  }, [isInheritingPriority, selectedTc]);

  // Build an inheritance map for all app classes (for the menu badges)
  const inheritanceMap = useMemo(() => {
    const map = {};
    for (const tc of TRAFFIC_CLASSES) {
      if (!tc.isApp || !tc.category) continue;
      const explicit = pendingMap[tc.key] || desiredMap[tc.key] || [];
      if (explicit.length > 0) {
        map[tc.key] = { inheriting: false, category: tc.category };
      } else {
        const catList = pendingMap[tc.category] || desiredMap[tc.category] || [];
        map[tc.key] = { inheriting: catList.length > 0, category: tc.category, catWanCount: catList.length };
      }
    }
    return map;
  }, [pendingMap, desiredMap]);

  const selectedCurrent = useMemo(
    () => currentMap[selectedTc] || [],
    [currentMap, selectedTc]
  );

  // Per-class "which interface is carrying this traffic right now" map, keyed
  // by class.key → active WAN name. Sourced from the `active` flag the edge
  // sets on each /tc_wan_priority/current entry (the first up & non-degraded
  // preferred WAN — i.e. the one the BPF data path actually picks). App
  // classes with no explicit rule inherit their category's active interface.
  const activeWanMap = useMemo(() => {
    const activeFrom = (list) => {
      if (!Array.isArray(list)) return null;
      const hit = list.find((e) => e && e.active);
      return hit ? hit.wan : null;
    };
    const map = {};
    for (const tc of TRAFFIC_CLASSES) {
      let name = activeFrom(currentMap[tc.key]);
      if (!name && tc.isApp && tc.category) {
        name = activeFrom(currentMap[tc.category]);
      }
      if (name) map[tc.key] = name;
    }
    return map;
  }, [currentMap]);

  // Per-class BOND flag (class.key → bool). App classes with no explicit action
  // inherit their category's action; everything defaults to bond. "spread" and
  // "stripe" are the retired mode names — an edge running an older build can
  // still report them, and they now mean the same single bonding action.
  const spreadMap = useMemo(() => {
    const actionFor = (key, category) => {
      const a = policyActions[key] ?? (category ? policyActions[category] : undefined);
      return ["bond", "spread", "stripe"].includes(a ?? "bond");
    };
    const map = {};
    for (const tc of TRAFFIC_CLASSES) map[tc.key] = actionFor(tc.key, tc.category);
    return map;
  }, [policyActions]);

  // Per-class live bonded set (class.key → [wan names]) — the healthy WANs a
  // spreading class is actually hashed across right now. Filters the current
  // ordering to enabled && non-degraded entries; app→category fallback.
  const bondedWanMap = useMemo(() => {
    const liveFrom = (list) => {
      if (!Array.isArray(list)) return null;
      const live = list.filter((e) => e && e.enabled !== false && !e.degraded).map((e) => e.wan);
      return live.length ? live : null;
    };
    const map = {};
    for (const tc of TRAFFIC_CLASSES) {
      let live = liveFrom(currentMap[tc.key]);
      if (!live && tc.isApp && tc.category) live = liveFrom(currentMap[tc.category]);
      if (live) map[tc.key] = live;
    }
    return map;
  }, [currentMap]);
  const filteredClasses = useMemo(() => {
    if (classFilter === "category") return TRAFFIC_CLASSES.filter((c) => !c.isApp);
    if (classFilter === "app") return TRAFFIC_CLASSES.filter((c) => c.isApp);
    return TRAFFIC_CLASSES;
  }, [classFilter]);
  const totalNodes = nodes.length;

  useEffect(() => { if (!IS_SINGLE_EDGE) syncNode(selectedNode); }, [selectedNode]);

  const selectedNodeObj = useMemo(
    () => nodes.find((n) => n.node_id === selectedNode) || null,
    [nodes, selectedNode]
  );
  // A phone answers a read-only part of the edge API over its reverse
  // channel, so with one selected the navigation offers only the screens that
  // part serves (the `phone` gate in navigation.js).
  const mobileSelected = isMobileNode(selectedNodeObj);
  // A phone that is heartbeating but holds no channel runs an app from before
  // phones answered the console, which is not the same as a phone that is
  // simply not connected, and the fix is different.
  const phoneAppOutdated =
    mobileSelected && selectedNodeObj?.stale === false && selectedNodeObj?.channel === false;

  // Edge mode: anchored (tunneled bonding) or direct (kernel-route NAT).
  // Prefer the live /healthz read (always current) and fall back to the
  // server-side registry hint so the sidebar gates correctly even before
  // the first /healthz round-trip lands.
  const nodeMode = health.mode || selectedNodeObj?.mode || "anchored";

  // A screen this edge's mode or this build does not offer, reached by a deep
  // link or left open while switching edges, falls back to the dashboard
  // rather than stranding the operator where the sidebar shows nothing.
  useEffect(() => {
    if (!viewOffered(activeView, { mode: nodeMode, singleEdge: IS_SINGLE_EDGE, phone: mobileSelected })) {
      setActiveView("dashboard");
    }
  }, [nodeMode, activeView, mobileSelected]);

  // The places the phone layout's bar offers this box on this build. A screen
  // opened there starts at its top, as a phone's app does.
  const places = phonePlaces({ mode: nodeMode, singleEdge: IS_SINGLE_EDGE, phone: mobileSelected, server: !IS_SINGLE_EDGE && session.managed });
  const goPlace = (key) => {
    setMobileNavOpen(false);
    if (key === "approval") openApproval("");
    else setActiveView(key);
  };
  useEffect(() => {
    if (phoneLayout && mainRef.current) mainRef.current.scrollTop = 0;
  }, [activeView, phoneLayout]);

  // Whether the currently-selected node is reachable through its API proxy.
  // We treat "unknown" (initial / pre-first-probe) as reachable so panels
  // still get a chance to load. Once /healthz consecutively fails we mark
  // the node "down" and drop all secondary edge polling so the rest of the
  // UI (fleet overview, sidebar, navigation) stays responsive.
  const nodeReachable = health.status !== "down";

  // True when a node is selected and it isn't responding. Used to skip
  // rendering its panels so they don't fire stuck fetches against an
  // unreachable proxy.
  const edgeUnavailable = !!selectedNode && !nodeReachable;

  // Traffic shaping is read from every reachable edge, whatever screen is
  // open, for the class badges. A phone has none, and every one of those
  // reads would cross its channel on someone's data plan.
  const shapingReadable = nodeReachable && !mobileSelected;

  // What every category does with the lines right now, in the box's own
  // answer: held to the line it is active on, or bonded across the live ones.
  // WAN Health labels its lines from this rather than from its health ranking,
  // so it cannot disagree with Traffic Shaping. Null until the box has
  // answered for every category, and in direct mode, where the kernel route
  // chooses the line and no class policy does.
  const lineRouting = useMemo(() => {
    const cats = TRAFFIC_CLASSES.filter((c) => !c.isApp);
    if (nodeMode === "direct" || !Object.keys(policyActions).length || cats.some((c) => !currentMap[c.key])) {
      return null;
    }
    return cats.map((c) => (spreadMap[c.key]
      ? { bond: true, lines: bondedWanMap[c.key] || [] }
      : { bond: false, lines: activeWanMap[c.key] ? [activeWanMap[c.key]] : [] }));
  }, [nodeMode, policyActions, currentMap, spreadMap, bondedWanMap, activeWanMap]);

  const renderEdgeUnavailable = (feature) => (
    <EdgeUnreachableNotice
      nodeId={selectedNode}
      feature={feature}
      phone={mobileSelected}
      appOutdated={phoneAppOutdated}
      onGoToFleet={viewOffered("nodes", { singleEdge: IS_SINGLE_EDGE }) ? () => setActiveView("nodes") : undefined}
    />
  );

  // ── Nodes polling ──────────────────────────────────────────────────────
  useEffect(() => {
    // Single-edge mode: this dashboard is hosted by one device. There is no
    // notabox-server and no node list — synthesize a single local node so the
    // edge panels render against the same-origin /console/api.
    if (IS_SINGLE_EDGE) {
      setNodes([
        {
          node_id: LOCAL_NODE_ID,
          mode: "anchored",
          // Non-empty api_url so nodeKind treats this as a full edge
          // controller (not a mobile client). The actual URL is unused —
          // nodeApiUrl() short-circuits to the same-origin /console/api.
          api_url: "console",
        },
      ]);
      setSelectedNode(LOCAL_NODE_ID);
      setNodesLoading(false);
      setLastNodesUpdate(new Date());
      return;
    }
    let active = true;
    let pollingInterval = null;

    const loadNodes = async () => {
      try {
        setNodesError("");
        const url = serverApiUrl("/nodes");
        const res = await fetchWithTimeout(url);
        // The fleet poll is the dashboard's heartbeat, so it is also where a
        // session ending mid-visit shows up first. Hand it back to the gate
        // rather than filling every panel with 401s.
        if (res.status === 401) {
          session.refresh();
          return;
        }
        if (!res.ok) {
          const errText = await res.text();
          throw new Error(`${res.status}: ${errText.substring(0, 100)}`);
        }
        const data = await res.json();
        // Filter anchors once, here, rather than in each panel — every view
        // downstream (fleet tiles, event log, node picker, counts) reads this
        // one list and none of them can act on a cloud anchor.
        const list = (Array.isArray(data?.nodes) ? data.nodes : []).filter(
          (n) => !isAnchorNode(n)
        );
        list.sort((a, b) => (a.node_id || "").localeCompare(b.node_id || ""));

        if (active) {
          setNodes(list);
          setLastNodesUpdate(new Date());
          setSelectedNode((prev) => {
            if (list.length === 0) return "";
            if (!prev || !list.some((node) => node.node_id === prev)) {
              return list[0].node_id;
            }
            return prev;
          });
        }
      } catch (err) {
        logError("Nodes load error", err);
        if (active) setNodesError(err.message || "Failed to load nodes");
      } finally {
        if (active) setNodesLoading(false);
      }
    };

    setNodesLoading(true);
    loadNodes();
    pollingInterval = setInterval(() => {
      if (active) loadNodes();
    }, 3000);

    return () => {
      active = false;
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [resolvedApiBase, resolvedProxyBase]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── WAN + health polling ───────────────────────────────────────────────
  useEffect(() => {
    if (!selectedNode) {
      setLoading(false);
      setInterfaces([]);
      setHealth({ status: "unknown" });
      return;
    }

    let active = true;
    let refreshTimer = null;
    let consecutiveFailures = 0;
    const FAIL_THRESHOLD = 3; // require N consecutive failures before marking "down"

    const load = async () => {
      try {
        setError("");
        // Probe /healthz and /wans in parallel. /healthz drives the
        // reachability indicator; /wans is best-effort and we only mark
        // the node "down" when /healthz fails repeatedly.
        const [healthRes, wansRes] = await Promise.all([
          fetchWithTimeout(nodeApiUrl("/healthz")),
          fetchWithTimeout(nodeApiUrl("/wans")).catch((e) => e),
        ]);

        if (!active) return;

        if (healthRes && healthRes.ok) {
          consecutiveFailures = 0;
          // /healthz now returns {status, mode} — parse so we can gate UI
          // by edge mode without polling /direct/status separately.
          let mode = "anchored";
          try {
            const body = await healthRes.json();
            if (body && typeof body.mode === "string" && body.mode) {
              mode = body.mode;
            }
          } catch {
            // older edges return {} or non-JSON — fall through with default
          }
          setHealth({ status: "ok", mode });
        } else {
          consecutiveFailures++;
          if (consecutiveFailures >= FAIL_THRESHOLD) {
            setHealth({ status: "down" });
          }
        }

        if (wansRes && typeof wansRes.ok === "boolean" && wansRes.ok) {
          const wansData = await wansRes.json();
          setInterfaces(Array.isArray(wansData?.wans) ? wansData.wans : []);
        }
        // /wans failures are non-fatal — keep previous interfaces list.
      } catch (err) {
        logError("Load error", err);
        if (active) {
          consecutiveFailures++;
          if (consecutiveFailures >= FAIL_THRESHOLD) {
            setHealth({ status: "down" });
            setError(err.message || "Failed to load data");
          }
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    setLoading(true);
    load();
    refreshTimer = setInterval(() => {
      if (active) load();
    }, 5000);

    return () => {
      active = false;
      if (refreshTimer) clearInterval(refreshTimer);
    };
  }, [selectedNode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── TC data helpers ────────────────────────────────────────────────────
  const parsePriorityList = (items) =>
    (Array.isArray(items) ? items : [])
      .slice()
      .sort((a, b) => (a.priority || 0) - (b.priority || 0));

  // The box's refusal is its own sentence: a JSON reason or error, else the
  // plain text an http.Error writes.
  const fetchJsonAt = async (target, options) => {
    if (!target) throw new Error("No node selected");
    const res = await fetchWithTimeout(target, options);
    if (!res.ok) {
      const text = (await res.text().catch(() => "")).trim();
      let reason = text;
      try {
        const payload = JSON.parse(text);
        reason = payload?.reason || payload?.error || text;
      } catch { /* plain text */ }
      throw new Error(reason || `Request failed (${res.status})`);
    }
    if (res.status === 204) return null;
    return res.json();
  };

  const fetchJson = (path, options) => fetchJsonAt(nodeApiUrl(path), options);

  const loadTcData = async (tc) => {
    try {
      setError("");
      const [currentRes, desiredRes] = await Promise.all([
        fetchJson(`/tc_wan_priority/current?tc=${encodeURIComponent(tc)}`),
        fetchJson(`/tc_wan_priority/desired?tc=${encodeURIComponent(tc)}`),
      ]);

      const currentPriorities = parsePriorityList(currentRes?.wan_priorities);
      const desiredPriorities = parsePriorityList(desiredRes?.wan_priorities);
      const desiredNames = desiredPriorities.map((entry) => entry.wan);

      setCurrentMap((prev) => ({ ...prev, [tc]: currentPriorities }));
      setDesiredMap((prev) => ({ ...prev, [tc]: desiredNames }));
      // pendingMap is left alone: the five-second poll lands here too, and an
      // order still on its way to the box must not flick back to the old one.
      // The writer clears it once the box answers with the new order.
    } catch (err) {
      logError("TC load error", err);
      setError(err.message || "Failed to load TC priorities");
    }
  };

  // ── Preload ALL category priorities when a node is selected ─────────
  useEffect(() => {
    if (!selectedNode) { setCategoryPrioritiesLoaded(false); return; }
    // Orders on their way to the previous node are not this node's — prevents
    // stale data (e.g. ghost interfaces like ewan0). The live orders go too:
    // until this node answers, the previous one's active lines would label
    // this node's links, and most boxes name theirs alike.
    setPendingMap({});
    setCurrentMap({});
    // Forget per-class customizations from the previous node; precedence is
    // tracked per session/node only.
    setCustomizedClasses(new Set());
    // Skip preload while node is unreachable; the load will retry when
    // /healthz recovers (this effect re-runs on selectedNode change, and
    // the per-tc poll below picks up new data once reachable again).
    if (!shapingReadable) { setCategoryPrioritiesLoaded(false); return; }
    let active = true;
    const cats = TRAFFIC_CLASSES.filter((c) => !c.isApp).map((c) => c.key);
    (async () => {
      try {
        await Promise.all(cats.map((cat) => loadTcData(cat)));
      } catch { /* errors are already handled inside loadTcData */ }
      if (active) setCategoryPrioritiesLoaded(true);
    })();
    return () => { active = false; };
  }, [selectedNode, shapingReadable]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedTc || !selectedNode) return;
    if (!shapingReadable) return; // don't pile up stuck fetches when down
    let active = true;
    let refreshTimer = null;

    const load = async () => {
      if (!active) return;
      await loadTcData(selectedTc);
    };

    load();
    refreshTimer = setInterval(() => {
      if (active) load();
    }, 5000);

    return () => {
      active = false;
      if (refreshTimer) clearInterval(refreshTimer);
    };
  }, [selectedTc, selectedNode, shapingReadable]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Keep the "active interface" fresh for EVERY class, not just the open
  //    one ───────────────────────────────────────────────────────────────
  // The effect above only refreshes the currently selected class, so the
  // Traffic Shaping orbs/badges for the OTHER classes would show a stale
  // active interface until you navigated onto them. Here we poll the
  // lightweight `current` list (which carries the per-class `active` flag the
  // BPF data path actually uses) for ALL categories every 5s so the carrier
  // chip updates live everywhere. App classes inherit their category's active
  // interface, so refreshing categories keeps the whole wheel in sync. Only
  // `current` is fetched — `desired` only matters for the class being edited.
  useEffect(() => {
    if (!selectedNode || !shapingReadable) return;
    let active = true;
    const cats = TRAFFIC_CLASSES.filter((c) => !c.isApp).map((c) => c.key);
    const refreshAllCurrents = async () => {
      const results = await Promise.all(
        cats.map(async (cat) => {
          try {
            const res = await fetchJson(
              `/tc_wan_priority/current?tc=${encodeURIComponent(cat)}`
            );
            return [cat, parsePriorityList(res?.wan_priorities)];
          } catch {
            return null;
          }
        })
      );
      if (!active) return;
      setCurrentMap((prev) => {
        const next = { ...prev };
        for (const r of results) {
          if (r) next[r[0]] = r[1];
        }
        return next;
      });
    };
    refreshAllCurrents();
    const timer = setInterval(refreshAllCurrents, 5000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [selectedNode, shapingReadable]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Traffic stats polling ──────────────────────────────────────────────
  useEffect(() => {
    if (!selectedNode || !shapingReadable) return;
    let active = true;
    const pollStats = async () => {
      try {
        const res = await fetchWithTimeout(nodeApiUrl("/stats"));
        if (res.ok && active) {
          const data = await res.json();
          setTrafficStats(data);
        }
      } catch { /* non-critical */ }
    };
    pollStats();
    const timer = setInterval(pollStats, 5000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [selectedNode, shapingReadable]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll the per-class scheduling action (spread|failover) so the WAN Priority
  // editor's mode picker reflects live control-plane state and picks up changes
  // made from the Bonding panel or elsewhere. Best-effort; legacy edges without
  // /policy/action just leave every class at the spread default.
  useEffect(() => {
    setPolicyActions({}); // the previous node's actions describe the previous node
    if (!selectedNode || !shapingReadable) return;
    let active = true;
    const load = async () => {
      try {
        const res = await fetchWithTimeout(nodeApiUrl("/policy/action"));
        if (!res.ok || !active) return;
        const data = await res.json();
        if (active) setPolicyActions(data?.actions || {});
      } catch { /* non-critical */ }
    };
    load();
    const timer = setInterval(load, 5000);
    return () => { active = false; clearInterval(timer); };
  }, [selectedNode, shapingReadable]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── WAN priority actions ───────────────────────────────────────────────
  const availableWans = useMemo(() => {
    const selected = new Set(selectedDesired);
    return interfaces.filter((wan) => !selected.has(wan.name));
  }, [interfaces, selectedDesired]);

  // Every change to a class's connections is applied as it is made: there is
  // no draft and no Apply, so the editor always shows what the box does. One
  // writer works through the changes in order, and a class changed again
  // before its turn sends only its latest list, so a burst of drags never
  // interleaves on the box. A change is written to the node it was made on,
  // and only that node's screen is updated from it.
  const writeQueue = useRef(new Map()); // `${node}\n${tc}` → { node, tc, list }
  const writing = useRef(false);
  const shownNode = useRef(selectedNode);
  const shownTc = useRef(selectedTc);
  useEffect(() => { shownNode.current = selectedNode; }, [selectedNode]);
  useEffect(() => { shownTc.current = selectedTc; }, [selectedTc]);

  const writeOrders = async () => {
    writing.current = true;
    try {
      while (writeQueue.current.size) {
        const [key, { node, tc, list }] = writeQueue.current.entries().next().value;
        writeQueue.current.delete(key);
        const url = nodeApiUrlFor(node);
        const here = () => shownNode.current === node;
        const again = () => writeQueue.current.has(key);
        try {
          await fetchJsonAt(url("/tc_wan_priority/desired"), {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ tc, wans_priority: list }),
          });
          // Changes are live on the PUT; boxes that predate that still wait for this.
          await fetchJsonAt(url("/apply_tc_wan_priority/desired"), { method: "POST" });
          if (!here()) continue;
          // A class with its own order is one the baseline order leaves alone;
          // an app emptied back to nothing follows its category again.
          setCustomizedClasses((prev) => {
            const n = new Set(prev);
            if (list.length) n.add(tc); else n.delete(tc);
            return n;
          });
          if (again()) continue;
          await loadTcData(tc);
          setPendingMap((prev) => {
            if (again() || !(tc in prev)) return prev;
            const n = { ...prev };
            delete n[tc];
            return n;
          });
          // The line under the editor follows the class changed last.
          setOrderSave((s) => (s.tc !== tc || again() ? s : { tc, state: "saved", error: "" }));
        } catch (err) {
          logError("WAN order error", err);
          // A newer change to the class still goes out, and stands on its own.
          if (!here() || again()) continue;
          // The box did not take it, so the editor goes back to what it holds.
          setPendingMap((prev) => {
            const n = { ...prev };
            delete n[tc];
            return n;
          });
          await loadTcData(tc);
          const why = err.message || "The box did not take that change.";
          setOrderSave({ tc, state: "failed", error: why });
          // Said where it happened; a failure on a class no longer open is said here too.
          if (shownTc.current !== tc) toast.error(`${TRAFFIC_CLASSES.find((c) => c.key === tc)?.label || tc}: ${why}`);
        }
      }
    } finally {
      writing.current = false;
    }
  };

  // applyOrder shows a class's new list at once and sends it to the box.
  const applyOrder = (tc, list) => {
    if (!tc || !selectedNode) return;
    const next = Array.from(new Set(list)).slice(0, 9);
    setPendingMap((prev) => ({ ...prev, [tc]: next }));
    writeQueue.current.set(`${selectedNode}\n${tc}`, { node: selectedNode, tc, list: next });
    setOrderSave({ tc, state: "saving", error: "" });
    if (!writing.current) writeOrders();
  };

  // Set the scheduling MODE for one traffic class from the WAN Priority editor.
  // bond=true  → aggregate flows across the selected connections, weighted by
  //              each link's measured capacity (order ignored).
  // bond=false → FAILOVER: use the selected connections in order (top-first).
  // Backed by PUT /policy/action, which keeps the choice across restarts.
  const setClassMode = async (classKey, bond) => {
    if (!classKey) return;
    setModeBusyTc(classKey);
    setError("");
    try {
      const action = bond ? "bond" : "failover";
      await fetchJson("/policy/action", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ class: classKey, action }),
      });
      setPolicyActions((prev) => ({ ...prev, [classKey]: action }));
      toast.success(`${classKey} set to ${action}`);
    } catch (err) {
      logError("Set mode error", err);
      toast.error(err.message || "Failed to change mode");
      setError(err.message || "Failed to change mode");
    } finally {
      setModeBusyTc(null);
    }
  };

  // An app's own order cleared, so it follows its category again: an empty
  // list, through the same writer as every other change.
  const clearAppOverride = () => {
    const tcDef = TRAFFIC_CLASSES.find((c) => c.key === selectedTc);
    if (tcDef?.isApp) applyOrder(selectedTc, []);
  };

  const resetRoutingRules = async () => {
    if (!selectedNode) return;
    const ok = await confirm({
      title: "Reset routing rules?",
      message: "Every kind of traffic goes back to the box's own choice: which connections it uses, in what order, and whether it bonds or fails over. Encryption is left as it is.",
      confirmLabel: "Reset",
      variant: "danger",
    });
    if (!ok) return;
    try {
      setResetting(true);
      setError("");
      await fetchJson("/tc_wan_priority/reset", { method: "POST" });
      setPendingMap({});
      setCustomizedClasses(new Set());
      const cats = TRAFFIC_CLASSES.filter((c) => !c.isApp).map((c) => c.key);
      await Promise.all([...new Set([...cats, selectedTc])].map((k) => loadTcData(k)));
      toast.success("Routing rules reset");
    } catch (err) {
      logError("Reset error", err);
      toast.error(err.message || "Failed to reset routing rules");
      setError(err.message || "Failed to reset routing rules");
    } finally {
      setResetting(false);
    }
  };

  // The baseline order currently in effect on this edge: the one WAN order that
  // every category the operator has not individually customized agrees on.
  //
  // This is exactly what applyBulkOrder writes, so reading it back off the
  // already-loaded per-class orders is what makes the setting look kept rather
  // than forgotten — the editor used to open on an empty list every time the
  // panel remounted, which reads as "my change did not save". Categories that
  // disagree mean no single baseline is in force, and an empty list is the
  // honest answer there.
  const baselineOrder = useMemo(() => {
    if (!categoryPrioritiesLoaded) return [];
    const cats = TRAFFIC_CLASSES
      .filter((c) => !c.isApp)
      .map((c) => c.key)
      .filter((k) => !customizedClasses.has(k));
    if (cats.length === 0) return [];
    const orders = cats.map((k) => desiredMap[k]);
    if (orders.some((o) => !Array.isArray(o) || o.length === 0)) return [];
    const first = orders[0].join(">");
    return orders.every((o) => o.join(">") === first) ? orders[0] : [];
  }, [categoryPrioritiesLoaded, customizedClasses, desiredMap]);

  // Apply a single baseline WAN order to every traffic category at once.
  // This is a convenience for setting a sensible default without ordering each
  // class by hand. It never touches categories the operator has individually
  // customized this session — those individual rules take precedence. Apps with
  // no explicit rule inherit their category, so they pick up the baseline too;
  // apps with their own override keep it. QoS-driven failover is unaffected
  // (it reorders degraded WANs at the data path regardless of this order).
  //
  // IT ALSO SETS EVERY TARGETED CATEGORY TO FAILOVER, because an order is
  // meaningless without it: BOND splits flows by measured capacity (pkg/bond)
  // and never consults the list's order, so writing an order to a bonded class
  // changes nothing while the panel claims "top = first choice". Turning
  // bonding off across the board is a real consequence, so it is spelled out in
  // the panel and again in the confirmation, not buried in a tooltip.
  const applyBulkOrder = async (order) => {
    if (!selectedNode) return;
    const cleanOrder = Array.from(new Set(order || [])).filter(Boolean);
    if (cleanOrder.length === 0) {
      toast.warning("Add at least one connection to the baseline order.");
      return;
    }
    const cats = TRAFFIC_CLASSES.filter((c) => !c.isApp).map((c) => c.key);
    const targets = cats.filter((k) => !customizedClasses.has(k));
    const skipped = cats.filter((k) => customizedClasses.has(k));
    if (targets.length === 0) {
      toast.warning("Every category is individually customized — nothing to set.");
      return;
    }
    const bonded = targets.filter((k) => (policyActions[k] ?? "bond") !== "failover");
    const ok = await confirm({
      title: "Set baseline order and turn bonding OFF?",
      message:
        (bonded.length
          ? `THIS DISABLES BONDING. ${bonded.length} categor${bonded.length === 1 ? "y is" : "ies are"} currently bonded, ` +
            "aggregating traffic across every connection at once. They will be switched to failover, " +
            "which uses one connection at a time and moves to the next only when it goes offline. " +
            "Aggregate throughput drops to that of a single link. "
          : "") +
        `Order applied to ${targets.length} categor${targets.length === 1 ? "y" : "ies"}: ${cleanOrder.join(" → ")}.` +
        (skipped.length
          ? ` ${skipped.length} individually-customized categor${skipped.length === 1 ? "y is" : "ies are"} kept as-is.`
          : "") +
        " Apps without their own rule follow their category automatically." +
        " Any category can be put back on bonding one at a time from its own editor.",
      confirmLabel: bonded.length ? "Disable bonding and apply" : "Apply to all",
      variant: "danger",
    });
    if (!ok) return;
    try {
      setBulkApplying(true);
      setError("");
      for (const k of targets) {
        await fetchJson("/tc_wan_priority/desired", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tc: k, wans_priority: cleanOrder }),
        });
      }
      // Failover before the apply, so the order the data path picks up is
      // already governed by the action that reads it.
      for (const k of targets) {
        await fetchJson("/policy/action", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ class: k, action: "failover" }),
        });
      }
      setPolicyActions((prev) => {
        const next = { ...prev };
        for (const k of targets) next[k] = "failover";
        return next;
      });
      await fetchJson("/apply_tc_wan_priority/desired", { method: "POST" });
      // Refresh every category so the wheel reflects the new order immediately.
      await Promise.all(targets.map((k) => loadTcData(k)));
      toast.success(
        `Baseline order applied to ${targets.length} categor${targets.length === 1 ? "y" : "ies"}; bonding off`
      );
    } catch (err) {
      logError("Bulk apply error", err);
      toast.error(err.message || "Failed to apply baseline order");
      setError(err.message || "Failed to apply baseline order");
    } finally {
      setBulkApplying(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────

  // Ada runs on an edge; a phone carries no assistant.
  const ada = !mobileSelected && <AdaButton selectedNode={selectedNode} />;

  return (
    <WanNamesProvider nodeId={selectedNode}>
    <div className="app-shell">
      {/* The header bar on a narrow screen. In the phone layout the bar at
          the foot opens the menu, so Ask Ada sits here in its place. */}
      <div className="mobile-header">
        {phoneLayout ? (
          <img className="mobile-mark" src={`${process.env.PUBLIC_URL}/sidebar-logo-w.png`} alt="Weavian" width="26" height="26" />
        ) : (
          <>
            <button className="mobile-hamburger" onClick={() => setMobileNavOpen(true)} aria-label="Open navigation">
              <FiMenu size={22} />
            </button>
            <span className="mobile-brand">Weavian</span>
          </>
        )}
        <NodeMenu
          placement="header"
          nodes={nodes}
          selectedNode={selectedNode}
          onSelect={setSelectedNode}
          health={health}
          singleEdge={IS_SINGLE_EDGE}
        />
        {phoneLayout && ada}
      </div>

      {/* Ada can hand over a walkthrough of the real controls; it drives the
          panel selection itself so each step lands on the right screen. */}
      <TourOverlay onPanel={setActiveView} nodeApiUrl={nodeApiUrl} />

      <Sidebar
        activeView={activeView}
        onNavigate={setActiveView}
        boxMenu={(
          <NodeMenu
            nodes={nodes}
            selectedNode={selectedNode}
            onSelect={setSelectedNode}
            health={health}
            singleEdge={IS_SINGLE_EDGE}
          />
        )}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
        mobileOpen={mobileNavOpen}
        onCloseMobile={() => setMobileNavOpen(false)}
        nodeMode={nodeMode}
        singleEdge={IS_SINGLE_EDGE}
        phone={mobileSelected}
      />

      <div className="app-main" ref={mainRef}>
        <div className="app-topbar">
          <RegionMenu regions={REGIONS} singleEdge={IS_SINGLE_EDGE} />
          {!phoneLayout && ada}
        </div>
        {/* On a phone the bar's Needs you counts what waits on every screen,
            and the strip says what it is where the console opens. */}
        {activeView !== "approval" && (!phoneLayout || activeView === HOME) && (
          <NeedsYou approvals={waiting.approvals} onOpen={openApproval} />
        )}
        {nodeMode === "direct" && selectedNode && !edgeUnavailable && (
          <div
            className="px-4 py-2 text-sm border-b"
            style={{
              background: "rgba(245, 158, 11, 0.08)",
              borderBottomColor: "rgba(245, 158, 11, 0.25)",
              color: "rgb(252, 211, 77)",
            }}
            role="status"
          >
            <strong>Direct mode</strong> — this edge bypasses the cloud anchor
            on the data path. LAN traffic egresses straight out the active WAN;
            no traffic shaping, no per-class routing, no stable inbound IP.
          </div>
        )}
        <div className="app-view-transition" key={activeView}>
        {/* ──────── Dashboard ──────── */}
        {activeView === "dashboard" && (edgeUnavailable
          ? renderEdgeUnavailable("Live edge dashboard")
          : (
          <div className="app-content">
            <DashboardView
              nodeApiUrl={nodeApiUrl}
              selectedNode={selectedNode}
              phone={mobileSelected}
              serverApiUrl={!IS_SINGLE_EDGE && session.managed ? serverApiUrl : undefined}
              onOpenUpdates={viewOffered("updates", { singleEdge: IS_SINGLE_EDGE }) ? () => setActiveView("updates") : undefined}
            />
          </div>
        ))}

        {/* ──────── Live Traffic ──────── */}
        {activeView === "interface-traffic" && (edgeUnavailable
          ? renderEdgeUnavailable("Live Interface Traffic")
          : (
          <div className="app-content">
            <div className="hero">
              <div>
                <h1>Live Interface Traffic</h1>
                <p>
                  Real-time throughput carried by each WAN interface. Switch a
                  policy and watch the traffic move between interfaces — the
                  line that climbs is the path now carrying the flow.
                </p>
              </div>
            </div>
            <InterfaceTrafficPanel
              nodeApiUrl={nodeApiUrl}
              selectedNode={selectedNode}
            />
          </div>
        ))}

        {/* ──────── WAN Health ──────── */}
        {activeView === "health" && (edgeUnavailable
          ? renderEdgeUnavailable("WAN Health Sensing")
          : (
          <div className="app-content">
            <div className="hero">
              <div>
                <h1>WAN Health Sensing</h1>
                <p>
                  Always-on latency, jitter and packet-loss probes run
                  end-to-end to the cloud anchor — the primary failover signal.
                  An optional throughput tier adds bandwidth-collapse detection.
                  Composite scores steer each traffic class onto the best path.
                </p>
              </div>
            </div>
            <HealthPanel
              nodeApiUrl={nodeApiUrl}
              selectedNode={selectedNode}
              lineRouting={lineRouting}
            />
          </div>
        ))}

        {/* ──────── Sequence-model shadow predictor (trial) ──────── */}
        {activeView === "seqforecast" && (edgeUnavailable
          ? renderEdgeUnavailable("AI Prediction (Trial)")
          : (
          <div className="app-content">
            <div className="hero">
              <div>
                <h1>AI Prediction (Trial)</h1>
                <p>
                  A small CNN trained on the raw per-WAN health time series,
                  running in shadow mode alongside the baseline heuristic —
                  read-only, does not influence routing. Compares its
                  degradation-probability prediction against the existing
                  forecaster's own verdict for evaluation.
                </p>
              </div>
            </div>
            <SeqForecastPanel
              nodeApiUrl={nodeApiUrl}
              selectedNode={selectedNode}
            />
          </div>
        ))}

        {/* ──────── Bonding Gain ──────── */}
        {activeView === "bonding" && (edgeUnavailable
          ? renderEdgeUnavailable("Bonding Gain")
          : (
          <div className="app-content">
            <div className="hero">
              <div>
                <h1>Bonding Gain</h1>
                <p>
                  Live link aggregation across the tunnel: total throughput, how
                  it splits across the WANs, the bonding-gain multiplier versus a
                  single link, and the bonded-capacity ceiling. Bonding is
                  per-flow, so the multiplier climbs above 1.0× only under many
                  concurrent flows — a busy venue AP, not a single download.
                </p>
              </div>
            </div>
            <BondingPanel
              nodeApiUrl={nodeApiUrl}
              selectedNode={selectedNode}
            />
          </div>
        ))}

        {/* ──────── Firewall ──────── */}
        {activeView === "firewall" && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Firewall</h1>
                <p>
                  Keep strangers out of this box and your network, and keep
                  chosen devices to the places they need.
                </p>
              </div>
            </section>
            {edgeUnavailable
              ? renderEdgeUnavailable("Firewall")
              : <FirewallPanel selectedNode={selectedNode} trafficClasses={TRAFFIC_CLASSES} nodes={nodes} />}
          </div>
        )}

        {/* ──────── Encryption ──────── */}
        {activeView === "encryption" && (edgeUnavailable
          ? renderEdgeUnavailable("Encryption")
          : (
          <div className="app-content">
            <div className="hero">
              <div>
                <h1>Encryption</h1>
                <p>
                  Seal the traffic inside the tunnel. Choose how strong, then
                  choose what gets it — encryption is per kind of traffic, so
                  you never have to pay for it on everything.
                </p>
              </div>
            </div>
            <EncryptionPanel
              nodeApiUrl={nodeApiUrl}
              selectedNode={selectedNode}
              trafficClasses={TRAFFIC_CLASSES}
            />
          </div>
        ))}

        {/* ──────── Obfuscation ──────── */}
        {activeView === "obfuscation" && (edgeUnavailable
          ? renderEdgeUnavailable("Obfuscation")
          : (
          <div className="app-content">
            <div className="hero">
              <div>
                <h1>Obfuscation</h1>
                <p>
                  Encryption hides what you send. This hides the shape of it —
                  that the tunnel is here, which link carried what, when you
                  were busy, and where you came out.
                </p>
              </div>
            </div>
            <ObfuscationPanel nodeApiUrl={nodeApiUrl}
                              onNavigate={setActiveView} />
          </div>
        ))}

        {/* ──────── Where Flows Exit ──────── */}
        {activeView === "flow-exits" && (edgeUnavailable
          ? renderEdgeUnavailable("Where Flows Exit")
          : (
          <div className="app-content">
            <div className="hero">
              <div>
                <h1>Where Flows Exit</h1>
                <p>
                  Each connection leaves through one exit and keeps it until it
                  closes. This is where they are going right now, measured by
                  volume rather than by connection count — three exits holding
                  one connection each look balanced while the one carrying the
                  transfer sees everything.
                </p>
              </div>
            </div>
            <FlowExitsPanel nodeApiUrl={nodeApiUrl} selectedNode={selectedNode} />
          </div>
        ))}

        {/* ──────── Agent Rules (the rules agents are judged by, and the freeze) ──────── */}
        {activeView === "agent-rules" && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Agent Rules</h1>
                <p>{RULES_HERO}</p>
              </div>
            </section>
            <AgentRulesPanel serverApiUrl={serverApiUrl} />
          </div>
        )}

        {/* ──────── WAN Priority (direct mode) ──────── */}
        {activeView === "wan-priority" && (edgeUnavailable
          ? renderEdgeUnavailable("WAN Priority")
          : (
          <div className="app-content">
            <WanPriorityPanel
              nodeApiUrl={nodeApiUrl}
              selectedNode={selectedNode}
            />
          </div>
        ))}

        {/* ──────── Traffic Shaping ──────── */}
        {activeView === "traffic" && (edgeUnavailable
          ? renderEdgeUnavailable("Traffic Shaping")
          : (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>
                  {selectedNode
                    ? "Shape WAN priorities by traffic class"
                    : "Edge Fleet Overview"}
                </h1>
                <p>
                  {selectedNode
                    ? "Choose a traffic type and order the connections it should use. Each change takes effect as you make it."
                    : "Monitor your registered edge devices. Select a node to manage traffic shaping."}
                </p>
              </div>
            </section>

            <div className="panel action-panel">
              <div className="panel-title">For the whole box</div>
              <ClassifierSetting nodeApiUrl={nodeApiUrl} selectedNode={selectedNode} />
              <div className="action-row">
                <button
                  className="danger-btn"
                  type="button"
                  onClick={resetRoutingRules}
                  disabled={resetting || !selectedNode}
                >
                  {resetting ? "Resetting…" : "Reset routing rules"}
                </button>
                <div className="panel-note">
                  Every kind of traffic goes back to the box's own choice of connections.
                </div>
              </div>
            </div>

            {!selectedNode && (
              <OverviewPanel totalNodes={totalNodes} lastUpdated={lastNodesUpdate} />
            )}

            <section className="grid">
              <TrafficPanels
                trafficClasses={filteredClasses}
                classFilter={classFilter}
                setClassFilter={setClassFilter}
                trafficStats={trafficStats}
                selectedNode={selectedNode}
                selectedTc={selectedTc}
                setSelectedTc={setSelectedTc}
                selectedCurrent={selectedCurrent}
                activeWanMap={activeWanMap}
                spreadMap={spreadMap}
                bondedWanMap={bondedWanMap}
                interfaces={interfaces}
                loading={loading}
                availableWans={availableWans}
                selectedDesired={selectedDesired}
                applyOrder={(list) => applyOrder(selectedTc, list)}
                orderSave={orderSave.tc === selectedTc ? orderSave : null}
                error={error}
                isInheritingPriority={isInheritingPriority}
                inheritedFromCategory={inheritedFromCategory}
                inheritanceMap={inheritanceMap}
                clearAppOverride={clearAppOverride}
                applyBulkOrder={applyBulkOrder}
                bulkApplying={bulkApplying}
                baselineOrder={baselineOrder}
                customizedClasses={customizedClasses}
                setClassMode={setClassMode}
                modeBusy={modeBusyTc === selectedTc}
              />
            </section>
          </div>
        ))}

        {/* ──────── Cloud Gateway ──────── */}
        {activeView === "cloud" && (edgeUnavailable
          ? renderEdgeUnavailable("Cloud Gateway")
          : (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Cloud Gateway</h1>
                <p>
                  See the cloud gateways this box can use and switch between them.
                  New connections go through the one you pick; connections already open stay where they started.
                </p>
              </div>
            </section>
            <CloudPanel
              nodeApiUrl={nodeApiUrl}
              selectedNode={selectedNode}
            />
          </div>
        ))}

        {/* ──────── Node Summary ──────── */}
        {activeView === "nodes" && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Fleet Overview</h1>
                <p>
                  All registered edge nodes at a glance. Expand any node
                  to inspect TUN addressing, system stats, WAN links,
                  and connected devices.
                </p>
              </div>
            </section>
            <NodeSummaryPanel
              nodes={nodes}
              nodesLoading={nodesLoading}
              nodesError={nodesError}
              proxyBase={resolvedProxyBase}
              onOpenSite={(id) => { setSelectedNode(id); setActiveView("dashboard"); }}
            />
          </div>
        )}

        {/* ──────── Remote Devices (a device at one site, reached from others) ──────── */}
        {activeView === "remote-devices" && (
          <div className="app-content app-content-canvas">
            <section className="hero">
              <div>
                <h1>Remote Devices</h1>
                <p>
                  Let computers at one site reach a device at another, such
                  as an old terminal nobody can reconfigure. Each site reaches
                  it at an address of its own, and nothing on the device
                  changes.
                </p>
              </div>
            </section>
            <RemoteDevicesPanel serverApiUrl={serverApiUrl} nodes={nodes} />
          </div>
        )}

        {/* ──────── Access Control (accounts, domains, edge assignment) ──────── */}
        {activeView === "access" && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Access Control</h1>
                <p>{ACCESS_LEAD}</p>
              </div>
            </section>
            <AccessPanel serverApiUrl={serverApiUrl} nodes={nodes} />
          </div>
        )}

        {/* ──────── Alerts (who is told when a site or line stops working) ──────── */}
        {activeView === "alerts" && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Alerts</h1>
                <p>
                  Sign up and you are emailed when one of your sites stops
                  working, and again when it is back. The Weavian server watches
                  every site, so nobody has to be watching this screen.
                </p>
              </div>
            </section>
            <AlertsPanel serverApiUrl={serverApiUrl} />
          </div>
        )}

        {/* ──────── Assistant (each customer's own key for Ada) ──────── */}
        {activeView === "assistant" && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Assistant</h1>
                <p>
                  Ada answers questions about your sites on your own Anthropic
                  account. Add your organisation's API key here: it is kept on
                  the Weavian server, never on your boxes.
                </p>
              </div>
            </section>
            <AssistantSettingsPanel serverApiUrl={serverApiUrl} />
          </div>
        )}

        {/* ──────── Software Updates (releases the boxes install themselves) ──────── */}
        {activeView === "updates" && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Software Updates</h1>
                <p>
                  Your boxes keep their own software up to date. Each installs a
                  new release in its site's update hour, checks it works, and
                  goes back to the version before by itself if it does not.
                </p>
              </div>
            </section>
            <UpdatesPanel serverApiUrl={serverApiUrl} />
          </div>
        )}

        {/* ──────── Investigations (the Agent Mesh's signed reads of a site) ──────── */}
        {activeView === "investigations" && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Investigations</h1>
                <p>
                  How each site reaches the internet, asked of its box and of
                  the Weavian gateway it goes through, every answer signed by
                  whoever gave it.
                </p>
              </div>
            </section>
            <InvestigationsPanel serverApiUrl={serverApiUrl} />
          </div>
        )}

        {/* ──────── Changes (a setting changed at a customer's sites, a site at a time) ──────── */}
        {activeView === "changes" && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Changes</h1>
                <p>{CHANGES_HERO}</p>
              </div>
            </section>
            <ChangesPanel serverApiUrl={serverApiUrl} trafficClasses={TRAFFIC_CLASSES} onOpenApproval={openApproval} />
          </div>
        )}

        {/* ──────── Trust Fabric (who may change the boxes, and the signed record) ──────── */}
        {/* The canvas takes the whole page: its own trail names the place. */}
        {activeView === "trust-fabric" && <TrustFabric serverApiUrl={serverApiUrl} />}

        {/* ──────── Outside Agents (keys for AWS DevOps Agent and others) ──────── */}
        {activeView === "outside-agents" && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Outside Agents</h1>
                <p>
                  Keys that let an AI agent outside Weavian, such as AWS DevOps
                  Agent, read how your sites reach the internet, and nothing
                  more.
                </p>
              </div>
            </section>
            <OutsideAgentsPanel serverApiUrl={serverApiUrl} />
          </div>
        )}

        {/* ──────── Approvers (who says yes to an agent's big changes, and their passkeys) ──────── */}
        {activeView === "approvers" && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Approvers</h1>
                <p>{APPROVERS_HERO}</p>
              </div>
            </section>
            <ApproversPanel serverApiUrl={serverApiUrl} />
          </div>
        )}

        {/* ──────── Needs you (a change an agent asked for, waiting for a yes) ──────── */}
        {activeView === "approval" && !IS_SINGLE_EDGE && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>{NEEDS_YOU}</h1>
                <p>{NEEDS_HERO}</p>
              </div>
            </section>
            <ApprovalPage serverApiUrl={serverApiUrl} id={approvalId} waiting={waiting} onOpen={openApproval} />
          </div>
        )}

        {/* ──────── Threat Intelligence (the fleet's shared attacker list) ──────── */}
        {activeView === "threat-intel" && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Threat Intelligence</h1>
                <p>
                  What every Weavian box blocks besides its own bans: attackers
                  other boxes banned, those Weavian's AI finds across sites, and
                  crowdsourced attacker lists. One list for every customer on
                  this control plane.
                </p>
              </div>
            </section>
            <ThreatIntelPanel serverApiUrl={serverApiUrl} />
          </div>
        )}

        {/* ──────── Your account (opened from the name in the sidebar) ──────── */}
        {activeView === "account" && session.user && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>{ACCOUNT}</h1>
                <p>{accountLead(session.user.username, session.role)}</p>
              </div>
            </section>
            <AccountPanel />
          </div>
        )}

        {/* ──────── Pilot Insights (server-side telemetry KPIs) ──────── */}
        {activeView === "pilot-insights" && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Pilot Insights</h1>
                <p>
                  Headline KPIs for the selected edge — uptime, failover
                  MTTR, QoS-in-SLA, top apps & devices, and a live event
                  feed. Computed by notabox-server from collected
                  telemetry, so it stays available even when the edge is
                  offline.
                </p>
              </div>
            </section>
            <PilotInsightsPanel
              selectedNode={selectedNode}
              serverApiUrl={serverApiUrl}
              phone={mobileSelected}
            />
          </div>
        )}

        {/* ──────── Power & Cost ──────── */}
        {activeView === "power" && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Power &amp; Cost</h1>
                <p>
                  What this appliance draws, what that costs to run for a
                  year, and what it emits. Measured on the box where the
                  hardware exposes a sensor; where it only exposes a
                  processor reading, the rest is modelled and labelled as
                  such.
                </p>
              </div>
            </section>
            {edgeUnavailable
              ? renderEdgeUnavailable("Power & Cost")
              : <PowerPanel selectedNode={selectedNode} />}
          </div>
        )}

        {activeView === "data-budgets" && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Data Budgets</h1>
                <p>
                  What each internet line has carried this month, against the
                  budget its plan allows. A line can be kept only as a backup
                  or switched off, by you or by its budget as the month's
                  usage climbs.
                </p>
              </div>
            </section>
            {edgeUnavailable
              ? renderEdgeUnavailable("Data Budgets")
              : <DataBudgetsPanel selectedNode={selectedNode} />}
          </div>
        )}

        {activeView === "device-limits" && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Device Speed Limits</h1>
                <p>
                  Give one device, or a group of them, its own speed limit.
                  A device you have not put anywhere uses the router's
                  settings, and new devices, a guest's phone included, can
                  join a profile the moment they connect.
                </p>
              </div>
            </section>
            {edgeUnavailable
              ? renderEdgeUnavailable("Device Speed Limits")
              : <DeviceLimitsPanel selectedNode={selectedNode} trafficClasses={TRAFFIC_CLASSES} />}
          </div>
        )}

        {/* ──────── Deep Telemetry ──────── */}
        {activeView === "deep-telemetry" && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Deep Telemetry</h1>
                <p>
                  Per-core CPU utilisation, NIC counters with errors and
                  drops, softnet (kernel-NAPI) drops, IRQ distribution and
                  per-thermal-zone temperatures — the same dimensions the
                  offline benchmark harness captures, exposed live and
                  downloadable as CSV.
                </p>
              </div>
            </section>
            <DeepTelemetryPanel
              selectedNode={selectedNode}
              serverApiUrl={serverApiUrl}
            />
          </div>
        )}

        {/* ──────── Activity Timeline ──────── */}
        {activeView === "events" && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Activity &amp; Events</h1>
                <p>
                  Fleet-wide event timeline aggregated across all edge nodes.
                  Filter by severity, node, or source to diagnose issues
                  and track changes.
                </p>
              </div>
            </section>
            <EventLogPanel
              nodes={nodes}
              proxyBase={resolvedProxyBase}
            />
          </div>
        )}

        {/* ──────── QoS Policies ──────── */}
        {activeView === "qos" && (edgeUnavailable
          ? renderEdgeUnavailable("QoS Policies")
          : (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>QoS Policies</h1>
                <p>
                  Application-aware quality thresholds for intelligent WAN switching.
                  Set per-app requirements so traffic is rerouted
                  when WAN quality degrades — without waiting for link failure.
                </p>
              </div>
            </section>
            <QoSPanel
              selectedNode={selectedNode}
              nodeApiUrl={nodeApiUrl}
            />
          </div>
        ))}

        {/* ──────── System Overview ──────── */}
        {activeView === "diag-overview" && (edgeUnavailable
          ? renderEdgeUnavailable("System Overview")
          : (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>System Overview</h1>
                <p>
                  Low-level diagnostics — system health gauges, tunnel stats,
                  classifier status, WAN health snapshot, and event log.
                </p>
              </div>
            </section>
            <DiagOverviewPanel
              nodeApiUrl={nodeApiUrl}
              selectedNode={selectedNode}
            />
          </div>
        ))}

        {/* ──────── WAN Deep Dive ──────── */}
        {activeView === "diag-wan" && (edgeUnavailable
          ? renderEdgeUnavailable("WAN Deep Dive")
          : (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>WAN Deep Dive</h1>
                <p>
                  Per-interface health metrics, packet counters, composite quality
                  scores, and active stress impairments — all in one view.
                </p>
              </div>
            </section>
            <DiagWANPanel
              nodeApiUrl={nodeApiUrl}
              selectedNode={selectedNode}
            />
          </div>
        ))}

        {/* ──────── Wi-Fi Across Homes ──────── */}
        {activeView === "home-wifi-fleet" && (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Wi-Fi Across Homes</h1>
                <p>
                  Every home whose box judges its Wi-Fi, the ones with a device struggling first, and whether the
                  trouble is the Wi-Fi, the home or the internet line. No device is named here: open a home to see its own.
                </p>
              </div>
            </section>
            <HomeWifiFleetPanel onOpenSite={(id) => { setSelectedNode(id); setActiveView("home-wifi"); }} />
          </div>
        )}

        {/* ──────── Home Wi-Fi ──────── */}
        {activeView === "home-wifi" && (edgeUnavailable
          ? renderEdgeUnavailable("Home Wi-Fi")
          : (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Home Wi-Fi</h1>
                <p>
                  How each device on this network is getting on, and whether the trouble is its
                  Wi-Fi, the home, or the internet line. Nothing needs installing on the devices.
                </p>
              </div>
            </section>
            <HomeWifiPanel selectedNode={selectedNode} />
          </div>
        ))}

        {/* ──────── Wi-Fi Networks ──────── */}
        {activeView === "wifi" && (edgeUnavailable
          ? renderEdgeUnavailable("Wi-Fi Networks")
          : (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Wi-Fi Networks</h1>
                <p>
                  The Wi-Fi networks this box can see and the one it is on.
                  Joining one gives the box another internet line.
                </p>
              </div>
            </section>
            <WifiPanel selectedNode={selectedNode} />
          </div>
        ))}

        {/* ──────── AS Path ──────── */}
        {activeView === "as-path" && (edgeUnavailable
          ? renderEdgeUnavailable("AS Path")
          : (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>AS Path</h1>
                <p>
                  Which networks each WAN's tunnel actually crosses to reach a
                  cloud anchor, and which ones the return traffic crosses coming
                  back. Probes leave from the tunnel's own source port, so the
                  hops are the hops the bonded traffic takes rather than a
                  generic traceroute's. Nobody can see a whole AS path from one
                  vantage point — MPLS hides transit, carrier NAT hides the first
                  hops, and routers rate-limit their replies — so every gap is
                  shown as a gap instead of being smoothed over.
                </p>
              </div>
            </section>
            <ASPathPanel nodeApiUrl={nodeApiUrl} />
          </div>
        ))}

        {/* ──────── Test Suites ──────── */}
        {activeView === "verify" && (edgeUnavailable
          ? renderEdgeUnavailable("Test Suites")
          : (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Test Suites</h1>
                <p>
                  Tests this edge runs on itself to show what it does. Pick one
                  to see how it works, run it, and read the result.
                </p>
              </div>
            </section>
            <VerificationPanel nodeApiUrl={nodeApiUrl} selectedNode={selectedNode} />
          </div>
        ))}

        {/* ──────── Devices & Flows ──────── */}
        {activeView === "diag-devices" && (edgeUnavailable
          ? renderEdgeUnavailable("Devices & Flows")
          : (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Devices &amp; Flows</h1>
                <p>
                  Connected LAN devices, per-device traffic counters, and
                  per-application flow breakdowns from the eBPF classifier.
                </p>
              </div>
            </section>
            <DiagDevicesPanel
              nodeApiUrl={nodeApiUrl}
              selectedNode={selectedNode}
            />
          </div>
        ))}

        {/* ──────── Remote Access (SSH) ──────── */}
        {activeView === "remote-access" && (edgeUnavailable
          ? renderEdgeUnavailable("Remote Access")
          : (
          <div className="app-content app-content-ssh">
            <section className="hero">
              <div>
                <h1>Remote Access</h1>
                <p>
                  Open a command line on this box from your browser, signed
                  in with one of the box's own accounts and its password.
                </p>
              </div>
            </section>
            <RemoteAccessPanel selectedNode={selectedNode} singleEdge={IS_SINGLE_EDGE} />
          </div>
        ))}

        {/* ──────── Dataset Collection ──────── */}
        {activeView === "dataset" && (edgeUnavailable
          ? renderEdgeUnavailable("Dataset Collection")
          : (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Dataset Collection</h1>
                <p>
                  The edge-side ML training-data flywheel. Each LAN flow's
                  statistical features are paired with its nDPI label and
                  written to a durable, self-bounding local archive. Confirm
                  collection is healthy and pull an on-demand sample to verify
                  exactly what is being stored — IPs are salted-hashed so the
                  dataset carries no PII.
                </p>
              </div>
            </section>
            <DatasetPanel
              nodeApiUrl={nodeApiUrl}
              selectedNode={selectedNode}
            />
          </div>
        ))}

        {/* ──────── Stress Tester ──────── */}
        {activeView === "stress-test" && (edgeUnavailable
          ? renderEdgeUnavailable("WAN Stress Tester")
          : (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>WAN Stress Tester</h1>
                <p>
                  Inject real network impairments — latency, packet loss,
                  bandwidth caps — on individual WAN interfaces to demonstrate
                  QoS-driven failover and traffic class migration in real time.
                </p>
              </div>
            </section>
            <StressTestPanel
              nodeApiUrl={nodeApiUrl}
              selectedNode={selectedNode}
            />
          </div>
        ))}

        {/* ──────── Connection Interruptions: where did a connection break? ──────── */}
        {activeView === "continuity" && (edgeUnavailable
          ? renderEdgeUnavailable("Connection Interruptions")
          : (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>Connection Interruptions</h1>
                <p>
                  When a call or connection freezes, this box records it: where
                  it broke, how sure we are, and what to do about it.
                </p>
              </div>
            </section>
            <FlowContinuityPanel
              nodeApiUrl={nodeApiUrl}
              selectedNode={selectedNode}
              trafficClasses={TRAFFIC_CLASSES}
            />
          </div>
        ))}

        {/* ──────── TAK Map Marker: is this box on the map? ──────── */}
        {activeView === "tak" && (edgeUnavailable
          ? renderEdgeUnavailable("TAK Map Marker")
          : (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>TAK Map Marker</h1>
                <p>
                  Put this appliance on your TAK map. It publishes itself to a
                  TAK server as one marker carrying the state of its circuits,
                  so anyone on that server sees the network they depend on
                  beside everything else they are tracking.
                </p>
              </div>
            </section>
            <TakPanel nodeApiUrl={nodeApiUrl} selectedNode={selectedNode} />
          </div>
        ))}

        {/* ──────── AQM / Bufferbloat Control ──────── */}
        {activeView === "aqm" && (edgeUnavailable
          ? renderEdgeUnavailable("AQM / Bufferbloat")
          : (
          <div className="app-content">
            <section className="hero">
              <div>
                <h1>AQM / Bufferbloat Control</h1>
                <p>
                  Install CAKE queueing on WAN uplinks so a link driven to
                  its capacity doesn't build a deep queue — the latency spike
                  that can otherwise starve this edge's own health probes and
                  knock a healthy WAN out of a SPREAD bond mid-transfer.
                </p>
              </div>
            </section>
            <AQMPanel
              nodeApiUrl={nodeApiUrl}
              selectedNode={selectedNode}
            />
          </div>
        ))}
        </div>
      </div>

      {/* ──────── The places on a phone, and More, which opens the sidebar as a sheet ──────── */}
      {phoneLayout && (
        <PhoneTabs
          places={places}
          view={activeView}
          moreOpen={mobileNavOpen}
          onGo={goPlace}
          onMore={() => setMobileNavOpen((v) => !v)}
          serverApiUrl={serverApiUrl}
          waiting={waiting.approvals?.length || 0}
        />
      )}
    </div>
    </WanNamesProvider>
  );
}

// The login gate is NOT here — index.js wraps every surface in it, so the
// landing chooser and Ada are gated too, and reaching this component already
// means there is a session. useSession() still resolves: the gate is a provider
// mounted above this tree.
export default function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <ConfirmProvider>
          <AppContent />
        </ConfirmProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}
